import { useQuery } from '@apollo/client'
import { GetPlanDataQuery } from '__generated__/graphql'
import { GET_PLAN_DATA_GQL } from './getPlanData.graphql'

/**
 * A shared hook for accessing plan data across components.
 * This hook always fetches fresh data from the network to ensure
 * plan information is up-to-date, as it may change frequently.
 *
 * @param options Optional configuration options
 * @returns Plan data query result
 */
export const usePlanData = (options?: {
  useCache?: boolean // Changed from skipCache to useCache for better semantics
}) => useQuery<GetPlanDataQuery>(GET_PLAN_DATA_GQL, {
  // Always fetch from network by default, unless explicitly told to use cache
  fetchPolicy: options?.useCache ? 'cache-first' : 'network-only',
  // Default options to ensure consistency across components
  errorPolicy: 'all',
  notifyOnNetworkStatusChange: true,
})

/**
 * Helper function to check if subscription is active
 */
export const isSubscriptionActive = (data?: GetPlanDataQuery) => data?.myEnterprise?.subscription?.__typename === 'BillingSubscriptionActive'

/**
 * Helper function to get features from plan data
 */
export const getFeatures = (data?: GetPlanDataQuery) => {
  if (!isSubscriptionActive(data)) return []

  const subscription = data?.myEnterprise?.subscription
  if (subscription?.__typename !== 'BillingSubscriptionActive') return []

  return subscription.plan?.product?.features || []
}
